import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Scanner } from '@yudiel/react-qr-scanner';
import type { IDetectedBarcode } from '@yudiel/react-qr-scanner/dist/types';
import { Camera, CameraIcon, FormIcon } from 'lucide-react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useCreateReceipt } from '@/api/receipt/use-create-receipt';
import type { CreateReceiptDto } from '@/api/types';
import FormInput from '@/components/form/form-input/form-input.tsx';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.tsx';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

const addReceiptSchema = z.object({
  receiptCode: z.string().min(1, 'Receipt code is required'),
});

type AddReceiptModalProps = {
  onClose: () => void;
};

const AddReceiptModal = ({ onClose }: AddReceiptModalProps) => {
  const [type, setType] = useState<'form' | 'scan'>('scan');

  const form = useForm<CreateReceiptDto>({ resolver: zodResolver(addReceiptSchema) });
  const code = form.watch('receiptCode');

  const { mutateAsync: createReceipt, isPending } = useCreateReceipt();

  const onSubmit = (data: CreateReceiptDto) => {
    createReceipt(data)
      .then(() => {
        onClose();
      })
      .catch(() => undefined);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  const handleScan = (detectedCodes: IDetectedBarcode[]) => {
    const code = detectedCodes.find((c) => c.format === 'qr_code');

    if (code?.rawValue) {
      form.setValue('receiptCode', code.rawValue);
    }
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <FormProvider {...form}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add New Receipt</DialogTitle>
            <DialogDescription>
              To add receipts, you can either enter the receipt code manually or use your camera to
              scan the QR code from your receipt.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center">
            <Tabs value={type} onChange={console.log}>
              <TabsList>
                <TabsTrigger value="form" onClick={() => setType('form')}>
                  <FormIcon />
                  Insert code
                </TabsTrigger>
                <TabsTrigger value="scan" onClick={() => setType('scan')}>
                  <CameraIcon />
                  Scan code
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          {type === 'form' && (
            <div>
              <FormInput name="receiptCode" label="Receipt code" required />
            </div>
          )}
          {type === 'scan' && !code && (
            <div className="border-2 border-dashed border-muted-foreground/20 rounded-lg p-6 text-center">
              <Scanner onScan={handleScan} onError={(error) => console.error(error)} />
            </div>
          )}
          {type === 'scan' && !!code && (
            <div className="border-2 border-dashed border-muted-foreground/20 rounded-lg p-6 text-center">
              <div className="space-y-3">
                <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto">
                  <Camera className="w-6 h-6 text-muted-foreground" />
                </div>
                <div>
                  <h3 className="font-medium text-sm">Code loaded</h3>
                  <p className="text-xs text-muted-foreground mt-1">{code}</p>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => form.setValue('receiptCode', '')}
                  className="w-full"
                >
                  <Camera className="size-4" />
                  Retry scanning
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => onClose()} variant="outline">
              Cancel
            </Button>
            <Button disabled={isPending} onClick={form.handleSubmit(onSubmit)} type="submit">
              Create receipt
            </Button>
          </DialogFooter>
        </DialogContent>
      </FormProvider>
    </Dialog>
  );
};

export default AddReceiptModal;
